import type { AppContribution } from "@/composition";
import { createRoute, Link, Outlet, useNavigate } from "@tanstack/solid-router";
import { createMemo, createSignal, For, type JSX, Show } from "solid-js";
import {
  BookMarked,
  BookOpen,
  ChevronsUpDown,
  GraduationCap,
  Menu,
  Trophy,
  User,
  UserCircle,
} from "@fleet/icons";
import { msg } from "@lingui/core/macro";
import type { MessageDescriptor } from "@lingui/core";

import { rootRoute } from "../__root";
import { sessionActor } from "@/lib/session-machine";
import { readActiveAccountSync } from "@/lib/accounts";
import { api, fetchApi } from "@/lib/api";
import { useSession } from "@/lib/auth";
import { useI18n } from "@/providers/i18n-provider";
import { AccountSwitcherHost } from "@/features/auth/account-switcher-host";
import { MobileNavDrawer } from "@/features/layout/components/mobile-nav-drawer";
import { useInteractionFlag } from "@fleet/ui";
import { queryClient } from "@/lib/query-client";
import { useKernel } from "@fleet/kernel";
import {
  cloneSession,
  type components,
  HTTP_FORBIDDEN,
  HTTP_UNAUTHORIZED,
  isErrorModel,
  isStatusError,
  isValidSession,
  queryKeys,
  type Session,
} from "@aula/shared";
import { Avatar } from "@/components/ui/avatar";
import { Logo } from "@/components/ui/logo";
import { validateAppLayoutSearch } from "@/features/layout/app-search";
import { SESSION_CHECK_TIMEOUT_MS, SESSION_STALE_TIME_MS } from "@/lib/constants";

interface NavItem {
  to: string;
  label: MessageDescriptor;
  icon: () => JSX.Element;
  testid: string;
}

/**
 * Navigation slot mounting a copy of navigation.
 *
 * Shell mounts the SAME navigation TWICE: desktop sidebar
 * (`hidden md:block`) and mobile drawer, which since the motion update is
 * **always in the DOM** to animate closing (unmounting node vanishes
 * in same frame). Both copies coexist in DOM at all
 * widths: one is hidden by CSS, and `display:none` does not remove it from
 * the document.
 *
 * Two nodes with the SAME `data-testid` make Playwright strict mode
 * error ("resolved to 2 elements") and break sidebar navigation.
 * Drawer copy suffixes its testids accordingly.
 */
type NavSlot = "desktop" | "drawer";

const slotTestId = (name: string, slot: NavSlot) => (slot === "drawer" ? `${name}-drawer` : name);

const NAV: NavItem[] = [
  {
    to: "/learn",
    label: msg`Learn`,
    icon: () => <GraduationCap class="h-5 w-5" />,
    testid: "nav-learn",
  },
  {
    to: "/catalog",
    label: msg`Catalog`,
    icon: () => <BookOpen class="h-5 w-5" />,
    testid: "nav-catalog",
  },
  {
    to: "/leaderboard",
    label: msg`Leagues`,
    icon: () => <Trophy class="h-5 w-5" />,
    testid: "nav-leaderboard",
  },
  {
    to: "/authoring",
    label: msg`Authoring`,
    icon: () => <BookMarked class="h-5 w-5" />,
    testid: "nav-authoring",
  },
  {
    to: "/profile",
    label: msg`Profile`,
    icon: () => <User class="h-5 w-5" />,
    testid: "nav-profile",
  },
];

/**
 * Aula shell: courses navigation (Learn / Catalog / Authoring) + Outlet.
 * Local-first and guest-first — content resolves offline; shell paints
 * immediately while PowerSync initializes.
 */
function AulaShell() {
  const [mobileOpen, setMobileOpen] = createSignal(false);
  // With drawer open, horizontal swipe is claimed (drag to close).
  // Any page swipe listens on window and would trigger underneath,
  // so it announces via interaction context rather than requiring each
  // gesture to query the DOM for an open drawer.
  useInteractionFlag({ "nav-drawer.open": mobileOpen });
  const session = useSession();
  const navigate = useNavigate();
  const { t } = useI18n();

  // URL-addressable switcher (#15b pattern): `?account=switch` opens it. The
  // overlay itself is mounted ONCE at the layout level (AccountSwitcherHost);
  // this trigger only flips the URL flag.
  const openSwitcher = () => {
    void navigate({
      to: ".",
      search: (prev) => ({ ...prev, account: "switch" }),
      replace: true,
    });
  };

  // Reactive view of the active account for the switcher trigger label. Reading
  // `session.data` registers the dependency so this recomputes when the session
  // changes (switch/sign-in re-primes it).
  const activeAccount = createMemo(() => {
    void session.data;
    return readActiveAccountSync();
  });

  const kernelApp = useKernel<AppContribution>();
  const navItems = createMemo(() => {
    const contributed = kernelApp()
      .contributions.filter((c) => c.kind === "nav")
      .map((c) => c.item)
      .toSorted((a, b) => a.order - b.order)
      .map((item) => {
        const Icon = item.icon;
        return {
          to: item.to,
          label: item.label,
          icon: () => <Icon class="h-5 w-5" />,
          testid: `nav-${item.id}`,
        };
      });
    return [...NAV, ...contributed];
  });

  const activeLabel = () => {
    const a = activeAccount();
    return a?.kind === "real" ? a.displayName : t(msg`Guest`);
  };

  const Sidebar = (props: { slot: NavSlot }) => (
    <nav
      class="flex h-full flex-col gap-1.5 p-3"
      data-testid={slotTestId("aula-sidebar", props.slot)}
    >
      <Link
        to="/learn"
        class="mb-3 flex items-center px-2 py-1 transition-transform hover:scale-[1.02] active:scale-95"
        onClick={() => setMobileOpen(false)}
      >
        <Logo />
      </Link>
      <For each={navItems()}>
        {(item) => (
          <Link
            to={item.to}
            data-testid={slotTestId(item.testid, props.slot)}
            onClick={() => setMobileOpen(false)}
            class="group flex items-center gap-3 rounded-2xl px-3 py-2.5 text-sm font-semibold text-muted-foreground transition-all hover:bg-accent/10 hover:text-accent [&.active]:bg-accent [&.active]:text-accent-foreground [&.active]:shadow-md [&.active]:shadow-accent/25"
            activeProps={{ class: "active" }}
          >
            <span class="transition-transform group-hover:scale-110 group-[.active]:scale-110">
              {item.icon()}
            </span>
            {t(item.label)}
          </Link>
        )}
      </For>

      {/* ── Account switcher (multi-account #18) ─────────────────────────────
          Trigger only: flips `?account=switch`; the overlay itself is mounted
          once at the layout level (AccountSwitcherHost, below). */}
      <button
        type="button"
        onClick={() => {
          setMobileOpen(false);
          openSwitcher();
        }}
        class="mt-auto flex w-full items-center gap-2.5 rounded-2xl border-t border-border/40 px-3 py-2.5 text-sm text-muted-foreground transition-colors hover:bg-accent/10 hover:text-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/30"
        aria-label={t(msg`Switch account`)}
        data-testid={slotTestId("account-switcher-trigger", props.slot)}
      >
        <Show
          when={activeAccount()?.kind === "real"}
          fallback={
            <span class="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-accent text-accent-foreground">
              <UserCircle class="h-4 w-4" />
            </span>
          }
        >
          <Avatar
            userId={activeAccount()?.userId}
            name={activeAccount()?.displayName}
            email={activeAccount()?.email}
            image={activeAccount()?.avatar}
            class="h-7 w-7 text-[10px]"
            testId={slotTestId("account-switcher-avatar", props.slot)}
          />
        </Show>
        <span class="min-w-0 flex-1 text-left">
          <span class="block truncate font-medium text-foreground">{activeLabel()}</span>
          <Show when={activeAccount()?.email}>
            <span class="block truncate text-xs text-muted-foreground/80">
              {activeAccount()?.email}
            </span>
          </Show>
        </span>
        <ChevronsUpDown class="h-4 w-4 shrink-0 opacity-60" />
      </button>
    </nav>
  );

  return (
    <div class="flex h-[100dvh] w-full overflow-hidden bg-background text-foreground">
      {/* Sidebar de escritorio */}
      <aside class="hidden w-60 shrink-0 border-r border-border bg-surface/40 md:block">
        <Sidebar slot="desktop" />
      </aside>

      {/* Drawer móvil (mismo mecanismo que el canon: siempre montado, entra y
          sale con `transform` en vez de montarse/desmontarse — ver
          mobile-nav-drawer.tsx). Su copia de la navegación sufija los testids
          (`slot="drawer"`) porque coexiste en el DOM con la de escritorio. */}
      <MobileNavDrawer isOpen={mobileOpen} setOpen={setMobileOpen} closeLabel={t(msg`Close`)}>
        <Sidebar slot="drawer" />
      </MobileNavDrawer>

      <div class="flex min-w-0 flex-1 flex-col">
        {/* Barra superior (móvil) */}
        <header class="flex items-center gap-2 border-b border-border p-2 md:hidden">
          <button
            type="button"
            data-testid="open-menu"
            onClick={() => setMobileOpen(true)}
            aria-label={t(msg`Menu`)}
            class="flex h-10 w-10 items-center justify-center rounded-xl text-muted-foreground transition-colors hover:bg-accent/10 hover:text-accent"
          >
            <Menu class="h-6 w-6" />
          </button>
          <Link
            to="/learn"
            class="flex items-center transition-transform hover:scale-[1.02] active:scale-95"
          >
            <Logo />
          </Link>
        </header>

        <main class="min-h-0 flex-1 overflow-y-auto" data-testid="aula-main">
          <Outlet />
        </main>
      </div>

      {/* URL-driven account switcher overlay (`?account=switch`) — single mount. */}
      <AccountSwitcherHost />
    </div>
  );
}

// Deduplicates concurrent beforeLoad calls during the page-load preload burst.
let activeSessionCheck: Promise<void> | null = null;

async function runSessionCheck(
  snapshot: ReturnType<typeof sessionActor.getSnapshot>,
): Promise<void> {
  if (snapshot.matches("priming")) {
    await new Promise<void>((resolve) => {
      const sub = sessionActor.subscribe((s) => {
        if (!s.matches("priming")) {
          sub.unsubscribe();
          resolve();
        }
      });
    });
  }

  // Multi-account: honor an explicit GUEST choice — don't verify with the server.
  if (readActiveAccountSync()?.kind === "guest") {
    return;
  }

  const currentSnapshot = sessionActor.getSnapshot();
  if (currentSnapshot.context.session || currentSnapshot.context.isVerified) {
    return;
  }

  const fetchSession = async () =>
    api.GET("/api/auth/get-session", { params: { query: { jwt: true } } });
  type FetchResult = Awaited<ReturnType<typeof fetchSession>>;

  const sessionResult = await (async () => {
    try {
      const timeoutPromise = new Promise<FetchResult>((_resolve, reject) => {
        setTimeout(() => {
          reject(new Error("Session check timeout"));
        }, SESSION_CHECK_TIMEOUT_MS);
      });
      return await Promise.race([
        api.GET("/api/auth/get-session", { params: { query: { jwt: true } } }),
        timeoutPromise,
      ]);
    } catch (err) {
      console.warn("[SessionCheck] Error or timeout verifying session:", err);
      return null;
    }
  })();

  if (!sessionResult) {
    return;
  }

  const { data, error, response } = sessionResult;

  let sessionPayload: Session | null = null;
  if (isValidSession(data)) {
    sessionPayload = cloneSession(data);
  }

  let errorPayload: components["schemas"]["ErrorModel"] | null = null;
  if (isErrorModel(error)) {
    errorPayload = error;
  }

  sessionActor.send({ type: "SERVER_REPLY", session: sessionPayload, error: errorPayload });

  if (error) {
    const status = isStatusError(error) ? error.status : response.status;
    if (status === HTTP_UNAUTHORIZED || status === HTTP_FORBIDDEN) {
      return;
    }
    return;
  }

  queryClient.setQueryData(queryKeys.auth.session(), data);
}

export const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "_app",
  validateSearch: validateAppLayoutSearch,
  beforeLoad: () => {
    const snapshot = sessionActor.getSnapshot();
    if (!snapshot.matches("priming") && snapshot.context.session) {
      const cached = queryClient.getQueryState(queryKeys.auth.session());
      const isStale =
        !cached?.dataUpdatedAt || Date.now() - cached.dataUpdatedAt > SESSION_STALE_TIME_MS;
      if (isStale) {
        void queryClient.prefetchQuery({
          queryKey: queryKeys.auth.session(),
          queryFn: async () =>
            fetchApi(api.GET("/api/auth/get-session", { params: { query: { jwt: true } } })),
          staleTime: SESSION_STALE_TIME_MS,
        });
      }
      return;
    }

    activeSessionCheck ??= (async () => {
      try {
        await runSessionCheck(snapshot);
      } finally {
        activeSessionCheck = null;
      }
    })();
  },

  component: AulaShell,
});
